/*
 * $Id:: index.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState } from 'react';

import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined';
import VisibilityOutlined from '@material-ui/icons/VisibilityOutlined';

import inputsMaxChars from '../../../../app/inputsMaxChars';
import ErrorInput from '../../../../components/ErrorInput';
import { t } from '../../../../providers';

const useStyles = makeStyles((theme) => ({
    PageWrapper: {
        display: 'flex',
        flexFlow: 'column nowrap',
    },
    inputWrapper: {
        marginBottom: '1.5rem',
    },
    textInput: {
        height: '2.5rem',
        width: '14rem',
        marginTop: '10px',
        '@media (max-width:600px)': {
            width: '12.5rem',
        },
    },
    tokenInput: {
        width: '22rem',
        '@media (max-width:600px)': {
            width: '100%',
        },
    },
    inputItem: {
        marginBottom: '10px',
        flex: '1 1 auto',
    },
    inputError: {
        marginTop: '5px',
    },
    fieldLabel: {
        fontWeight: theme.typography.titleFontWeight,
        fontSize: theme.typography.titleFontSize,
        color: theme.palette.text.mainGreen,
        marginTop: '1.5rem',
    },
    tokenLabel: {
        marginTop: '0',
    },
    verifyButton: {
        minWidth: '7.5rem',
        marginLeft: '1rem',
    },
    passwords: {
        marginTop: '1.5rem',
    },
}));

const VerificationForm = ({ token, verifyAccount, requestNewCode, error, includePassword }) => {
    const [code, setCode] = useState({ value: token || '', isDirty: false });
    const [password, setPassword] = useState({ value: '', showPassword: false });
    const [confirmPassword, setConfirmPassword] = useState({ value: '', showPassword: false });
    const [passwordsMatch, setPasswordsMatch] = useState(false);

    const requestNew = (event) => {
        event.preventDefault();
        requestNewCode();
    };

    const handleChangeValue = (event) => setCode({ value: event.target.value, isDirty: true });

    const submitCode = (event) => {
        event.preventDefault();
        let payload = { token: code.value };
        if (includePassword) {
            const isFormValid = code.value && password.value && passwordsMatch;
            if (!isFormValid) {
                setPassword({ ...password, isDirty: true, isBlured: true });
                setConfirmPassword({ ...confirmPassword, isDirty: true, isBlured: true });
                setCode({ ...code, isDirty: true });
                return;
            }
            payload.password = password.value;
        } else {
            if (!code.value) setCode({ ...code, isDirty: true });
        }
        verifyAccount(payload);
    };

    const handlePasswordChange = (event) => {
        const value = event.target.value;
        if (!inputsMaxChars.password || value.length <= inputsMaxChars.password) {
            setPassword({ ...password, value, isDirty: true });
            setPasswordsMatch(event.target.value === confirmPassword.value);
        }
    };

    const handleConfirmPasswordChange = (event) => {
        if (!inputsMaxChars.password || event.target.value.length <= inputsMaxChars.password) {
            setConfirmPassword({ ...password, value: event.target.value, isDirty: true });
            setPasswordsMatch(event.target.value === password.value);
        }
    };

    const handleOnBlurPassword = () => {
        if (!password.isBlured) setPassword({ ...password, isBlured: true });
    };

    const handleOnBlurConfirmPassword = () => {
        if (!confirmPassword.isBlured) setConfirmPassword({ ...confirmPassword, isBlured: true });
    };

    const toggleVisibility = () => setPassword({ ...password, showPassword: !password.showPassword });

    const toggleConfirmVisibility = () => setConfirmPassword({ ...confirmPassword, showPassword: !confirmPassword.showPassword });

    const passwordsDoNotMatch = !!password.value && password.isBlured && confirmPassword.isBlured && password.value !== confirmPassword.value;

    const passwordError = error === 'illegal.password' || '';

    const classes = useStyles();
    return (
        <div className={classes.PageWrapper}>
            <form onSubmit={submitCode} noValidate>
                <Grid container direction="column" className={classes.inputWrapper}>
                    <Grid item xs sm md className={classes.inputItem}>
                        <InputLabel
                            htmlFor="verificationCode"
                            role="input"
                            error={(!!error && !passwordError) || (code.isDirty && !code.value)}
                            className={`${classes.fieldLabel} ${classes.tokenLabel}`}>
                            {t('verification.code')}*
                        </InputLabel>
                        <Input
                            id="verificationCode"
                            role="input"
                            className={`${classes.textInput} ${classes.tokenInput}`}
                            placeholder={t('type.verification.code')}
                            type="verificationCode"
                            name="text"
                            fullWidth
                            autoFocus
                            onChange={handleChangeValue}
                            value={code.value}
                            error={(!!error && !passwordError) || (code.isDirty && !code.value)}
                        />
                        {code.isDirty && !code.value && <ErrorInput errorMessage={t('required.field')} classes={`${classes.inputError}`} />}
                    </Grid>
                    <Grid item className={classes.gridItem}>
                        <Typography variant="body2" className={classes.infoMessage}>
                            {t('verification.account.description')}
                        </Typography>
                    </Grid>
                    {includePassword && (
                        <>
                            <Grid item xs sm md className={classes.gridItem}>
                                <InputLabel
                                    role="input"
                                    htmlFor="password"
                                    error={!!passwordError || (password.isDirty && !password.value) || passwordsDoNotMatch}
                                    className={classes.fieldLabel}>
                                    {t('password')}*
                                </InputLabel>
                                <Input
                                    id="password"
                                    role="input"
                                    className={classes.textInput}
                                    placeholder={t('type.password')}
                                    type={password.showPassword ? 'text' : 'password'}
                                    name="password"
                                    fullWidth
                                    onChange={handlePasswordChange}
                                    onBlur={handleOnBlurPassword}
                                    value={password.value}
                                    endAdornment={
                                        <IconButton onClick={toggleVisibility} type="button" disableRipple>
                                            {password.showPassword ? (
                                                <VisibilityOffOutlined aria-hidden="true" focusable="false" />
                                            ) : (
                                                <VisibilityOutlined aria-hidden="true" focusable="false" />
                                            )}
                                        </IconButton>
                                    }
                                    error={!!passwordError || (password.isDirty && !password.value) || passwordsDoNotMatch}
                                />
                                {password.isDirty && !password.value && (
                                    <ErrorInput errorMessage={t('required.field')} classes={`${classes.inputError}`} />
                                )}
                                {passwordsDoNotMatch && <ErrorInput errorMessage={t('entering.same.value')} classes={`${classes.inputError}`} />}
                                <Typography variant="body2" className={`${classes.infoMessage} ${classes.messageUndelined}`}>
                                    {t('password.pattern.description')}
                                </Typography>
                            </Grid>
                            <Grid item xs sm md className={classes.gridItem}>
                                <InputLabel
                                    htmlFor="confirmPassword"
                                    role="input"
                                    error={!!passwordError || (confirmPassword.isDirty && !confirmPassword.value) || passwordsDoNotMatch}
                                    className={classes.fieldLabel}>
                                    {t('repeat.password')}*
                                </InputLabel>
                                <Input
                                    id="confirmPassword"
                                    role="input"
                                    className={classes.textInput}
                                    placeholder={t('type.password')}
                                    type={confirmPassword.showPassword ? 'text' : 'password'}
                                    name="confirmPassword"
                                    fullWidth
                                    onChange={handleConfirmPasswordChange}
                                    onBlur={handleOnBlurConfirmPassword}
                                    value={confirmPassword.value}
                                    endAdornment={
                                        <IconButton onClick={toggleConfirmVisibility} type="button" disableRipple>
                                            {confirmPassword.showPassword ? (
                                                <VisibilityOffOutlined aria-hidden="true" focusable="false" />
                                            ) : (
                                                <VisibilityOutlined aria-hidden="true" focusable="false" />
                                            )}
                                        </IconButton>
                                    }
                                    error={!!passwordError || (confirmPassword.isDirty && !confirmPassword.value) || passwordsDoNotMatch}
                                />
                                {confirmPassword.isDirty && !confirmPassword.value && (
                                    <ErrorInput errorMessage={t('required.field')} classes={`${classes.inputError}`} />
                                )}
                                <Typography variant="body2" className={classes.infoMessage}>
                                    {t('make.sure.matches')}
                                </Typography>
                                {!!passwordError && (
                                    <Grid item xs sm md>
                                        <ErrorInput errorMessage={t(passwordError)} classes={`${classes.infoMessage}`} />
                                    </Grid>
                                )}
                            </Grid>
                        </>
                    )}
                    {!!error && (
                        <Grid item xs sm md>
                            <Typography color="error">{t(error)}</Typography>
                        </Grid>
                    )}
                </Grid>
                <Grid container spacing={0}>
                    <Grid item>
                        <Button onClick={requestNew} variant="outlined" type="button">
                            {t('send.new.code')}
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" className={classes.verifyButton} type="submit" disabled={!code.value}>
                            {t('verify.account')}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </div>
    );
};

export default VerificationForm;
